import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Assuming your SVG logo is saved in public/logo.svg or accessible via a URL
// For local SVG, you might need to configure your bundler (e.g., Webpack with SVGR)
// For this example, I'll use the direct URL you provided.
const logoUrl = "https://uploads-ssl.webflow.com/602156e042193761b5577fce/602791e97df225621c20f05c_logo_kult-io_white.svg";
const ACTIVE_TEXT_COLOR = "text-white"; // Was previously border color, now for text
const INACTIVE_TEXT_COLOR = "text-gray-400";
const HOVER_TEXT_COLOR = "text-white";
const ACTIVE_BORDER_COLOR = "border-blue-400"; // blue-400
const INACTIVE_BORDER_COLOR = "border-transparent";
const HOVER_BORDER_COLOR = "border-gray-300";

const BUTTON_BG_COLOR = "#3B82F6"; // blue-500
const BUTTON_BG_HOVER_COLOR = "#2563EB"; // blue-600

// Updated navigation links
const navLinks = [
  { href: "#home", label: "Home", id: "home", pagePath: "/" },
  { href: "#services", label: "Services", id: "services", pagePath: "/" },
  { href: "#about", label: "About Us", id: "about", pagePath: "/" },
  { href: "#projects", label: "Projects", id: "projects", pagePath: "/" },
  { href: "/blog", label: "Blog", id: "blog", pagePath: "/blog" },
  { href: "#contact", label: "Contact", id: "contact" },
];

interface NavbarProps {
  onOpenContactModal: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onOpenContactModal }) => {
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();

  useEffect(() => {
    const { pathname, hash } = location;
    let scrollListenerAttached = false;

    const handleScroll = () => {
      let newActiveId = '';

      // --- Primary Candidate Search (section straddling 150px line) ---
      let primaryCandidateId = '';
      let maxVisibleHeightForPrimary = -1;

      navLinks.forEach(link => {
        if (link.pagePath === '/' && link.href.startsWith('#')) {
          const element = document.getElementById(link.id);
          if (element) {
            const rect = element.getBoundingClientRect();
            const windowHeight = window.innerHeight || document.documentElement.clientHeight;
            // Check if the section straddles the 150px line from the top
            if (rect.top <= 150 && rect.bottom >= 150) {
              const visibleTop = Math.max(0, rect.top);
              const visibleBottom = Math.min(windowHeight, rect.bottom);
              const visibleHeight = Math.max(0, visibleBottom - visibleTop);
              if (visibleHeight > maxVisibleHeightForPrimary) {
                maxVisibleHeightForPrimary = visibleHeight;
                primaryCandidateId = link.id;
              }
            }
          }
        }
      });

      if (primaryCandidateId) {
        newActiveId = primaryCandidateId;
      } else if (window.scrollY <= 50) {
        newActiveId = 'home';
      } else {
        let fallbackCandidateId = '';
        let minTopForFallback = Infinity;
        navLinks.forEach(link => {
          if (link.pagePath === '/' && link.href.startsWith('#')) {
            const element = document.getElementById(link.id);
            if (element) {
              const rect = element.getBoundingClientRect();
              // Check if the section is visible on screen
              if (rect.top < (window.innerHeight || document.documentElement.clientHeight) && rect.bottom > 0) {
                if (rect.top < minTopForFallback) {
                  minTopForFallback = rect.top;
                  fallbackCandidateId = link.id;
                }
              }
            }
          }
        });

        if (fallbackCandidateId) {
          newActiveId = fallbackCandidateId;
        } else {
          newActiveId = 'home'; // Absolute fallback if scrolled and no other section found
        }
      }

      // Use functional update to avoid stale closure on activeSection
      setActiveSection(currentActiveSection => {
        if (currentActiveSection !== newActiveId) {
          return newActiveId;
        }
        return currentActiveSection;
      });
    };

    if (pathname.startsWith('/blog')) {
      setActiveSection('blog');
    } else if (pathname === '/') {
      if (hash) {
        const elementId = hash.substring(1);
        const targetLink = navLinks.find(l => l.id === elementId && l.pagePath === '/');
        if (targetLink) {
          setActiveSection(targetLink.id);
          setTimeout(() => {
            document.getElementById(targetLink.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
      } else {
        handleScroll(); // Set initial active section on load for homepage
      }
      window.addEventListener('scroll', handleScroll, { passive: true });
      scrollListenerAttached = true;
    } else {
      setActiveSection(''); // For unknown paths
    }

    return () => {
      if (scrollListenerAttached) {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  }, [location]); // Only re-run this effect if location changes

  return (
    <nav className="bg-gray-900 border-b border-gray-800 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" onClick={() => setActiveSection('home')}>
                <img className="h-8 w-auto" src={logoUrl} alt="Kult.io Logo" />
              </Link>
            </div>
            <div className="hidden sm:ml-10 sm:flex sm:space-x-8">
              {navLinks.map((link) => {
                const isActive = activeSection === link.id;

                if (link.id === 'contact') {
                  return (
                    <button
                      key={link.id}
                      onClick={() => {
                        onOpenContactModal();
                        setActiveSection('contact');
                      }}
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium cursor-pointer 
                        ${
                          isActive 
                            ? `${ACTIVE_TEXT_COLOR} ${ACTIVE_BORDER_COLOR}` 
                            : `${INACTIVE_TEXT_COLOR} ${INACTIVE_BORDER_COLOR} hover:${HOVER_TEXT_COLOR} hover:${HOVER_BORDER_COLOR}`
                        }`}
                    >
                      {link.label}
                    </button>
                  );
                }
                
                // Determine the correct `to` prop for react-router Link
                let toPath: string;
                if (link.pagePath === '/' && link.href.startsWith('#')) {
                  // Homepage section link, e.g., /#services
                  toPath = `/${link.href}`;
                } else {
                  // Direct page link, e.g., /blog
                  toPath = link.pagePath!;
                }

                return (
                  <Link
                    key={link.id}
                    to={toPath}
                    onClick={() => {
                      // Set active section optimistically. 
                      // The useEffect will handle scrolling for hash links after navigation.
                      setActiveSection(link.id);
                    }}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium 
                      ${
                        isActive 
                          ? `${ACTIVE_TEXT_COLOR} ${ACTIVE_BORDER_COLOR}` 
                          : `${INACTIVE_TEXT_COLOR} ${INACTIVE_BORDER_COLOR} hover:${HOVER_TEXT_COLOR} hover:${HOVER_BORDER_COLOR}`
                      }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
          <div className="flex items-center">
            <button 
              onClick={() => {
                onOpenContactModal();
                setActiveSection('contact'); // Also set contact active here
              }}
              style={{ backgroundColor: BUTTON_BG_COLOR }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = BUTTON_BG_HOVER_COLOR}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = BUTTON_BG_COLOR}
              className="text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-150"
            >
              Get in Touch
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 